import React, { useEffect, useState, useCallback } from 'react';
import { ArrowRightLeft, User, RefreshCw } from 'lucide-react';
import { getHealth } from '../../services/api';
import type { HealthStatus } from '../../types';

const Header: React.FC = () => {
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [checking, setChecking] = useState(false);

  const checkHealth = useCallback(async () => {
    setChecking(true);
    try {
      const h = await getHealth();
      setHealth(h);
    } catch {
      setHealth(null);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    void checkHealth();
    const interval = setInterval(() => void checkHealth(), 30000);
    return () => clearInterval(interval);
  }, [checkHealth]);

  const isUp = health?.status === 'ok' || health?.status === 'healthy';

  return (
    <header className="h-14 bg-white border-b border-slate-200 flex items-center justify-between px-6 shadow-sm">
      {/* Left: Title */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 text-[#1e3a5f] font-bold text-base">
          <ArrowRightLeft size={18} className="text-blue-500" />
          Whitestone ETL Migration Tool
        </div>
        <span className="hidden md:block text-slate-400 text-sm">
          — Finacle 10.2.18 → 11.15
        </span>
      </div>

      {/* Right: Health + User */}
      <div className="flex items-center gap-4">
        {/* API Health */}
        <button
          onClick={() => void checkHealth()}
          className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors"
          title="Check API health"
        >
          {checking ? (
            <RefreshCw size={14} className="animate-spin text-slate-400" />
          ) : (
            <span
              className={`w-2 h-2 rounded-full ${
                isUp ? 'bg-green-500' : health === null ? 'bg-slate-300' : 'bg-red-500'
              }`}
            />
          )}
          <span className="hidden sm:inline">
            {checking
              ? 'Checking...'
              : isUp
              ? `API Online${health?.version ? ` v${health.version}` : ''}`
              : health === null
              ? 'Connecting...'
              : 'API Offline'}
          </span>
        </button>

        {/* Divider */}
        <div className="w-px h-6 bg-slate-200" />

        {/* User */}
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <div className="w-7 h-7 rounded-full bg-[#1e3a5f] flex items-center justify-center">
            <User size={14} className="text-white" />
          </div>
          <span className="hidden sm:inline font-medium">Admin</span>
        </div>
      </div>
    </header>
  );
};

export default Header;
